import {
	Scene,
	Vector3,
	Color4,
	ParticleSystem,
	Texture,
	type AbstractMesh,
	type Camera,
} from "@babylonjs/core";

export type WeatherKind = "clear" | "rain" | "snow";

export interface Weather {
	kind: WeatherKind;
	tick: (dt: number, follow: Camera) => void;
	setKind: (k: WeatherKind) => void;
	dispose: () => void;
	/** Multiplier applied to printer failure rate by gameplay code. */
	failureRateMultiplier: number;
}

function texture(scene: Scene) {
	const tex = new Texture(
		"data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNgAAIAAAUAAen63NgAAAAASUVORK5CYII=",
		scene,
	);
	tex.hasAlpha = true;
	return tex;
}

export function createWeather(scene: Scene, anchor: AbstractMesh): Weather {
	const rain = new ParticleSystem("rain", 2000, scene);
	rain.particleTexture = texture(scene);
	rain.emitter = anchor;
	rain.minEmitBox = new Vector3(-25, 20, -25);
	rain.maxEmitBox = new Vector3(25, 25, 25);
	rain.color1 = new Color4(0.6, 0.7, 0.9, 0.6);
	rain.color2 = new Color4(0.7, 0.8, 1.0, 0.7);
	rain.colorDead = new Color4(0.5, 0.6, 0.8, 0);
	rain.minSize = 0.03;
	rain.maxSize = 0.05;
	rain.minScaleY = 4;
	rain.maxScaleY = 8;
	rain.minLifeTime = 0.6;
	rain.maxLifeTime = 1.0;
	rain.emitRate = 1500;
	rain.gravity = new Vector3(0, -28, 0);
	rain.direction1 = new Vector3(-2, -1, -2);
	rain.direction2 = new Vector3(2, -1, 2);
	rain.minEmitPower = 0.1;
	rain.maxEmitPower = 0.3;
	rain.updateSpeed = 0.015;
	rain.stop();

	const snow = new ParticleSystem("snow", 1200, scene);
	snow.particleTexture = texture(scene);
	snow.emitter = anchor;
	snow.minEmitBox = new Vector3(-25, 18, -25);
	snow.maxEmitBox = new Vector3(25, 22, 25);
	snow.color1 = new Color4(1, 1, 1, 0.9);
	snow.color2 = new Color4(0.92, 0.95, 1, 0.85);
	snow.colorDead = new Color4(1, 1, 1, 0);
	snow.minSize = 0.08;
	snow.maxSize = 0.18;
	snow.minLifeTime = 4;
	snow.maxLifeTime = 6;
	snow.emitRate = 350;
	snow.gravity = new Vector3(0, -1.6, 0);
	snow.direction1 = new Vector3(-0.4, -0.8, -0.4);
	snow.direction2 = new Vector3(0.4, -0.4, 0.4);
	snow.minEmitPower = 0.1;
	snow.maxEmitPower = 0.4;
	snow.updateSpeed = 0.015;
	snow.stop();

	const w: Weather = {
		kind: "clear",
		failureRateMultiplier: 1.0,
		tick(_dt: number, follow: Camera) {
			anchor.position.x = follow.position.x;
			anchor.position.z = follow.position.z;
		},
		setKind(k) {
			if (k === w.kind) return;
			w.kind = k;
			rain.stop();
			snow.stop();
			if (k === "rain") {
				rain.start();
				w.failureRateMultiplier = 1.5; // wetter air → more nozzle issues
			} else if (k === "snow") {
				snow.start();
				w.failureRateMultiplier = 1.3;
			} else {
				w.failureRateMultiplier = 1.0;
			}
		},
		dispose() {
			rain.dispose();
			snow.dispose();
		},
	};
	return w;
}
